/**
 * Итоги прогона в разрезе токенов текста.
 *
 * Режим, который тренирует устойчивые сочетания, считает результат по
 * сочетанию целиком: `!=` — это одно движение, и разложить его на `!` и `=`
 * значит потерять ровно то, ради чего сочетание показано. Символьный разбор
 * здесь не годится, а поэлементный — тем более: в одном элементе текста
 * сочетание встречается вместе с другими.
 *
 * Пакет не знает, что стоит за токеном: сопоставление токена учебной единице
 * — дело продуктовой функции.
 */

import type { KeystrokeEvent, TypingError } from '@type2x/shared-contracts';

import { median } from './metrics.js';

/** Вхождение токена в текст: позиция считается в кодовых точках. */
export interface TokenSpan {
  readonly token: string;
  /** Позиция первого символа токена в тексте, с нуля. */
  readonly start: number;
  /** Длина токена в кодовых точках. */
  readonly length: number;
}

/** Итог прогона по одному токену. */
export interface TokenOutcome {
  readonly token: string;
  readonly attempts: number;
  readonly errors: number;
  readonly corrections: number;
  readonly medianLatencyMs: number;
}

/** Наблюдения по одному токену внутри прогона. */
interface Accumulator {
  attempts: number;
  errors: number;
  corrections: number;
  latencies: number[];
}

function emptyAccumulator(): Accumulator {
  return { attempts: 0, errors: 0, corrections: 0, latencies: [] };
}

/** Совпадает ли токен с текстом начиная с позиции. */
function matchesAt(characters: readonly string[], position: number, token: string): boolean {
  const wanted = [...token];

  return wanted.every((character, offset) => characters[position + offset] === character);
}

/**
 * Находит вхождения токенов в текст.
 *
 * Длинные токены проверяются первыми: иначе `==` распалось бы на два `=`, а
 * `<=` — на `<` и `=`, и составной оператор перестал бы существовать. По той
 * же причине найденное вхождение занимает свои позиции целиком: внутри `!=`
 * отдельного `=` уже нет.
 *
 * Символы, не входящие ни в один токен, пропускаются: текст режима содержит
 * и то, что тренирует другой режим.
 *
 * @param text текст прогона;
 * @param tokens токены, которые нужно найти.
 */
export function findTokenSpans(text: string, tokens: readonly string[]): TokenSpan[] {
  const ordered = [...new Set(tokens)]
    .filter((token) => token.length > 0)
    .sort((left, right) => [...right].length - [...left].length || left.localeCompare(right));

  const characters = [...text];
  const spans: TokenSpan[] = [];

  let position = 0;
  while (position < characters.length) {
    const token = ordered.find((candidate) => matchesAt(characters, position, candidate));

    if (token === undefined) {
      position += 1;
      continue;
    }

    const length = [...token].length;
    spans.push({ token, start: position, length });
    position += length;
  }

  return spans;
}

/**
 * Сопоставляет нажатия найденным вхождениям токенов.
 *
 * Вхождения одного токена складываются: в тексте он встречается не один раз,
 * а учебная единица у них одна.
 *
 * @param spans вхождения токенов в текст прогона;
 * @param events нажатия прогона;
 * @param errors ошибки прогона.
 */
export function collectTokenOutcomes(
  spans: readonly TokenSpan[],
  events: readonly KeystrokeEvent[],
  errors: readonly TypingError[],
): TokenOutcome[] {
  const errorPositions = new Set(errors.map((error) => error.position));
  const byToken = new Map<string, Accumulator>();

  for (const span of spans) {
    const accumulator = byToken.get(span.token) ?? emptyAccumulator();
    accumulator.attempts += span.length;

    for (let offset = 0; offset < span.length; offset += 1) {
      const position = span.start + offset;
      if (errorPositions.has(position)) accumulator.errors += 1;

      const event = events[position];
      if (event === undefined) continue;
      if (event.interKeyIntervalMs > 0) accumulator.latencies.push(event.interKeyIntervalMs);
      if (event.corrected) accumulator.corrections += 1;
    }

    byToken.set(span.token, accumulator);
  }

  return [...byToken.entries()].map(([token, accumulator]) => ({
    token,
    attempts: accumulator.attempts,
    errors: accumulator.errors,
    corrections: accumulator.corrections,
    medianLatencyMs: median(accumulator.latencies),
  }));
}
